import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Modal from '../shared/Modal';

const CreateSectionModal = ({ isOpen, onClose, writingSessionId, foresightSessionId, onSectionCreated, existingSectionsCount = 0 }) => {
  const [title, setTitle] = useState('');
  const [sectionType, setSectionType] = useState('custom');
  const [aiPrompt, setAiPrompt] = useState('');
  const [uncertaintyId, setUncertaintyId] = useState('');
  const [uncertainties, setUncertainties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [sectionPassword, setSectionPassword] = useState('');

  useEffect(() => {
    if (foresightSessionId && isOpen) {
      loadUncertainties();
    }
  }, [foresightSessionId, isOpen]);

  const loadUncertainties = async () => {
    try {
      const data = await api.getUncertainties(foresightSessionId);
      setUncertainties(data);
    } catch (error) {
      console.error('Failed to load uncertainties:', error);
    }
  };

  const sectionTypes = [
    { value: 'custom', label: 'Custom Section', description: 'General purpose writing section' },
    { value: 'uncertainty_deepdive', label: 'Uncertainty Deep Dive', description: 'Explore an uncertainty in detail' },
    { value: 'scenario', label: 'Scenario Development', description: 'Develop future scenarios' },
    { value: 'iofo', label: 'Image of Future Operations (IOFO)', description: 'Describe future operational state' },
  ];

  const defaultPrompts = {
    custom: 'You are a helpful assistant supporting a collaborative writing session. Help synthesize participant inputs into coherent content.',
    uncertainty_deepdive: 'You are a strategic foresight analyst. Help participants explore this uncertainty by asking probing questions, identifying implications, and connecting to broader trends.',
    scenario: 'You are a scenario planning expert. Help participants develop plausible future scenarios by identifying key drivers, critical uncertainties, and narrative elements.',
    iofo: 'You are a strategic operations planner. Help participants describe a vivid, concrete image of future operations by focusing on specific capabilities, processes, and outcomes.',
  };

  const handleTypeChange = (type) => {
    setSectionType(type);
    if (!aiPrompt || Object.values(defaultPrompts).includes(aiPrompt)) {
      setAiPrompt(defaultPrompts[type]);
    }
  };

  const handleCreate = async () => {
    if (!title) return;

    setIsLoading(true);
    try {
      const newSection = await api.createWritingSection({
        writing_session_id: writingSessionId,
        title,
        section_type: sectionType,
        ai_prompt: aiPrompt,
        uncertainty_id: uncertaintyId ? parseInt(uncertaintyId) : null,
        content: '',
        order: existingSectionsCount,
        section_metadata: {},
        is_password_protected: isPasswordProtected,
        section_password: isPasswordProtected ? sectionPassword : null,
      });

      onSectionCreated(newSection);
      
      // Reset form
      setTitle('');
      setSectionType('custom');
      setAiPrompt('');
      setUncertaintyId('');
      setIsPasswordProtected(false);
      setSectionPassword('');
    } catch (error) {
      console.error('Failed to create section:', error);
      alert('Failed to create section: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Section">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
            placeholder="e.g., Defining Dynamic Space Operations"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Section Type</label>
          <select
            value={sectionType}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
          >
            {sectionTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {sectionTypes.find(t => t.value === sectionType)?.description}
          </p>
        </div>

        {sectionType === 'uncertainty_deepdive' && uncertainties.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Uncertainty (Optional)
            </label>
            <select
              value={uncertaintyId}
              onChange={(e) => setUncertaintyId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
            >
              <option value="">Select an uncertainty...</option>
              {uncertainties.map(unc => (
                <option key={unc.id} value={unc.id}>
                  {unc.title}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AI Assistant Prompt
            <span className="text-xs text-gray-500 ml-2">(Customize how AI helps with this section)</span>
          </label>
          <textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] font-mono text-sm"
            rows="4"
            placeholder="You are an LLM whose task is to..."
          />
        </div>

        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isPasswordProtected}
              onChange={e => setIsPasswordProtected(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#01006A] focus:ring-[#01006A]"
            />
            <span className="text-sm font-medium text-gray-700">Password-protect this section</span>
          </label>
          {isPasswordProtected && (
            <input
              type="password"
              value={sectionPassword}
              onChange={e => setSectionPassword(e.target.value)}
              className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="Set a password for this section"
            />
          )}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleCreate}
            disabled={!title || isLoading}
            className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
          >
            {isLoading ? 'Creating...' : 'Create Section'}
          </button>
          <button
            onClick={() => {
              onClose();
              setTitle('');
              setSectionType('custom');
              setAiPrompt('');
              setUncertaintyId('');
              setIsPasswordProtected(false);
              setSectionPassword('');
            }}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSectionModal;